/**
 * Helper functions for interacting with AI services for photo book generation
 */
import axios from 'axios';

/**
 * Formats a date in a consistent, readable format for the AI
 * @param {*} timestamp - The timestamp to format (can be various formats)
 * @returns {string} Formatted date string
 */
export function formatDateForAI(timestamp) {
  if (!timestamp) return "";
  
  let date;
  if (typeof timestamp === 'object' && timestamp.seconds) {
    date = new Date(timestamp.seconds * 1000);
  } else if (typeof timestamp === 'string') {
    date = new Date(timestamp);
  } else {
    date = new Date(timestamp);
  }
  
  if (isNaN(date.getTime())) return "";
  
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).replace(',', '');
}

/**
 * Clean the entry data before sending to AI to reduce noise
 * @param {Object} entry - The story entry to clean
 * @param {Function} logFn - Optional logging function
 * @returns {Object} Cleaned entry
 */
/**
 * Estimates the word capacity of a text box based on its dimensions and font size
 * @param {Object} textStyle - The style object of the text element containing width, height and fontSize
 * @returns {number} Estimated word capacity
 */
function estimateWordCapacity(textStyle) {
  if (!textStyle || !textStyle.width || !textStyle.height || !textStyle.fontSize) {
    return 0;
  }
  try {
    const boxWidthPercent = parseFloat(textStyle.width); // e.g., 60 for "60%"
    const boxHeightPercent = parseFloat(textStyle.height); // e.g., 15 for "15%"
    const fontSizePx = parseFloat(textStyle.fontSize); // e.g., 17 for "17px"

    if (isNaN(boxWidthPercent) || isNaN(boxHeightPercent) || isNaN(fontSizePx) || fontSizePx <= 0) {
      return 0;
    }
    // Heuristic: (boxWidthPercent * boxHeightPercent * Constant_Factor) / (FontSizePx^2)
    // For 60% width, 15% height, 17px font:
    // (60 * 15 * 11.6) / (17^2) = (900 * 11.6) / 289 = 10440 / 289 = ~36 words.
    const words = Math.floor((boxWidthPercent * boxHeightPercent * 11.6) / (fontSizePx * fontSizePx));
    return Math.max(5, words); // Ensure at least a few words, e.g., for small text boxes or titles.
  } catch (e) {
    console.error("Error estimating word capacity:", e);
    return 0;
  }
}

export function cleanEntryForAI(entry, logFn) {
  if (!entry) return null;
  if (logFn) logFn('Cleaning entry data for AI prompt', { 
    type: 'DATA_CLEANING',
    entryId: entry.id,
    photoCount: entry.photos?.length || 0,
    timestamp: new Date().toISOString()
  });
  
  try {
    // Create a deep copy to avoid mutating the original
    const cleanedEntry = JSON.parse(JSON.stringify(entry));
    
    // Remove fields that don't help the AI
    delete cleanedEntry.pendingPhotos;
    delete cleanedEntry.updatedAt;
    delete cleanedEntry.draft;
    delete cleanedEntry.userId;
    delete cleanedEntry.aiRequestId;
    delete cleanedEntry.__id;
    delete cleanedEntry.__path;
    
    // Format createdAt if it exists
    if (cleanedEntry.createdAt) {
      cleanedEntry.formattedDate = formatDateForAI(cleanedEntry.createdAt);
    }
    
    // Clean up each photo
    if (cleanedEntry.photos && Array.isArray(cleanedEntry.photos)) {
      cleanedEntry.photos = cleanedEntry.photos.map(photo => {
        const cleanPhoto = { ...photo };
        
        const fieldsToDelete = [
          'original_photo_id',
          'productUrl',
          'localUri',
          'driveFileId',
          'filename',
          'mimeType',
          'baseUrl',
          'url',
          'cropInfo',
          'ai_reference_id',
          'mediaId',
          'isDriveFile',
          'journalEntryId'
        ];

        // Remove unnecessary fields
        for (const key of fieldsToDelete) {
          delete cleanPhoto[key];
        }

        if (cleanPhoto.mediaMetadata) {
          delete cleanPhoto.mediaMetadata['photo'];
        }

        // Format date_taken
        if (cleanPhoto.date_taken) {
          cleanPhoto.date_taken = formatDateForAI(cleanPhoto.date_taken);
        }
        
        // Remove size if it's "0x0"
        if (cleanPhoto.size === "0x0") {
          delete cleanPhoto.size;
        }
        
        // Clean up place information
        if (cleanPhoto.place) {
          const cleanPlace = { ...cleanPhoto.place };
          delete cleanPlace.place_id;
          
          // If place address is very long, truncate it
          if (cleanPlace.address && cleanPlace.address.length > 50) {
            const addressParts = cleanPlace.address.split(',');
            if (addressParts.length > 3) {
              // Keep only the city, state/province, and country
              cleanPlace.address = addressParts.slice(-3).join(',').trim();
            }
          }
          
          cleanPhoto.place = cleanPlace;
        }
        
        return cleanPhoto;
      });
      
      // Filter out photos with a score lower than 67
      const originalPhotoCount = cleanedEntry.photos.length;
      cleanedEntry.photos = cleanedEntry.photos.filter(photo => {
        // Check if photo has a score property and it's at least 67
        return !photo.score || parseInt(photo.score) >= 67;
      });
      
      if (logFn) logFn('Photo cleaning complete', { 
        originalCount: entry.photos.length,
        filteredCount: originalPhotoCount,
        filteredOutCount: originalPhotoCount - cleanedEntry.photos.length,
        cleanedCount: cleanedEntry.photos.length,
      });
    }
    
    return cleanedEntry;
  } catch (error) {
    if (logFn) logFn('Error cleaning entry data', { error: error.message });
    return entry; // Return original if cleaning failed
  }
}

/**
 * Analyze layouts and add AI hints based on layout characteristics
 * @param {Array} layoutsToProcess - Array of photo book layouts
 * @param {Function} logLayoutFn - Optional logging function
 * @returns {Array} Enhanced layouts with AI hints
 */
export function createEnhancedLayoutsForAI(layoutsToProcess, logLayoutFn) {
  if (logLayoutFn) logLayoutFn('Creating enhanced layouts with AI hints', { 
    type: 'LAYOUT_ENHANCEMENT',
    layoutCount: Object.keys(layoutsToProcess).length,
    timestamp: new Date().toISOString()
  });
  
  // Filter out cover page layouts
  const filteredLayouts = layoutsToProcess.filter(layout => { 
    const idIsCover = typeof layout.id === 'string' && layout.id.toLowerCase().includes('cover');
    const typeIsCover = layout.type && typeof layout.type === 'string' && layout.type.toLowerCase() === 'cover';
    return !idIsCover && !typeIsCover;
  });
  
  if (logLayoutFn && filteredLayouts.length !== layoutsToProcess.length) {
    logLayoutFn('Skipped cover page layout for AI selection', {
      originalCount: layoutsToProcess.length,
      filteredCount: filteredLayouts.length,
      skippedCount: layoutsToProcess.length - filteredLayouts.length
    });
  }
  
  return filteredLayouts.map(layout => {
    // Extract photo information for left and right pages
    const leftPagePhotos = layout.leftPage.photoPlaceholders || [];
    const rightPagePhotos = layout.rightPage.photoPlaceholders || [];
    
    const leftPagePhotoCount = leftPagePhotos.length;
    const rightPagePhotoCount = rightPagePhotos.length;
    const totalPhotos = leftPagePhotoCount + rightPagePhotoCount;
    
    // Analyze photo sizes and positions to determine layout characteristics
    const leftPageHasLargePhotos = leftPagePhotos.some(p => parseFloat(p.width) > 60);
    const rightPageHasLargePhotos = rightPagePhotos.some(p => parseFloat(p.width) > 60);
    const leftPageHasRotation = leftPagePhotos.some(p => p.rotation && p.rotation !== "0deg");
    const rightPageHasRotation = rightPagePhotos.some(p => p.rotation && p.rotation !== "0deg");
    
    // Check if layout has text area on left page
    const hasLeftPageText = layout.leftPage.text && true;
    const hasRightPageText = layout.rightPage.text && true;
    
    const leftPageTextSize = hasLeftPageText ? 
      (parseFloat(layout.leftPage.text.style.height) * parseFloat(layout.leftPage.text.style.width)) : 0;
    const rightPageTextSize = hasRightPageText ? 
      (parseFloat(layout.rightPage.text.style.height) * parseFloat(layout.rightPage.text.style.width)) : 0;
    
    // Estimate word capacity for text areas
    const leftPageMaxWords = layout.leftPage.text ? estimateWordCapacity(layout.leftPage.text.style) : 0;
    const rightPageMaxWords = layout.rightPage.text ? estimateWordCapacity(layout.rightPage.text.style) : 0;
    
    // Calculate textual description of the layout
    const leftPageDescription = {
      photoCount: leftPagePhotoCount,
      hasLargePhotos: leftPageHasLargePhotos,
      hasRotation: leftPageHasRotation,
      hasText: hasLeftPageText,
      maxWords: leftPageMaxWords,
      photoSizes: leftPagePhotos.map(p => ({ 
        width: p.width, 
        aspectRatio: p.aspectRatio,
        rotation: p.rotation
      }))
    };
    
    const rightPageDescription = {
      photoCount: rightPagePhotoCount,
      hasLargePhotos: rightPageHasLargePhotos,
      hasRotation: rightPageHasRotation,
      hasText: hasRightPageText,
      maxWords: rightPageMaxWords,
      photoSizes: rightPagePhotos.map(p => ({ 
        width: p.width, 
        aspectRatio: p.aspectRatio,
        rotation: p.rotation
      }))
    };

    // Generate AI hint based on layout characteristics
    let aiHint = '';
    const background = typeof layout.background === 'string' ? 
      (layout.background.includes('background') ? layout.background.split('/').pop() : layout.background) : 
      'solid color';
        
    if (totalPhotos === 1) {
      aiHint = "Best for highlighting a single standout photo that deserves full attention.";
    } else if (totalPhotos === 2) {
      if (leftPageHasLargePhotos && rightPageHasLargePhotos) {
        aiHint = "Ideal for displaying two equally important photos, such as before/after or comparison shots.";
      } else {
        aiHint = "Good for featuring a main photo with a supporting image, like a portrait with a scene.";
      }
    } else if (totalPhotos === 3) {
      if (leftPagePhotoCount === 1) {
        aiHint = "Perfect for a main highlight photo with supporting detail shots on the opposite page.";
      } else if (rightPagePhotoCount === 1) {
        aiHint = "Great for telling a story with supporting images leading to a conclusion shot.";
      } else {
        aiHint = "Balanced layout for showing multiple perspectives of a single event or scene.";
      }
    } else if (totalPhotos === 4) {
      aiHint = "Excellent for sequential storytelling or showing multiple aspects of an experience.";
    } else if (totalPhotos >= 5) {
      aiHint = "Best for collage-style presentation of related photos from an event or location.";
    }
    
    // Add background-specific hints
    if (background.includes('background1')) {
      aiHint += " Blue sea background works well with outdoor, water, or vacation photos.";
    } else if (background.includes('background2')) {
      aiHint += " Green zoo background suits nature, animals, or family outings.";
    } else if (background.includes('background3')) {
      aiHint += " Pink background is perfect for celebrations, birthdays, or cheerful moments.";
    } else if (background === "#f7f7f7") {
      aiHint += " Light neutral background emphasizes text and is great for reflective or detailed stories.";
    } else if (background === "#333333") {
      aiHint += " Dark background creates drama and works well for evening events or artistic shots.";
    }
    
    // Add text-specific hints
    if (hasLeftPageText && !hasRightPageText && leftPageTextSize > 0.2) {
      aiHint += ` Includes substantial text area on left page (~${leftPageMaxWords} words) for detailed storytelling.`;
    } else if (!hasLeftPageText && hasRightPageText && rightPageTextSize > 0.2) {
      aiHint += ` Features text area on right page (~${rightPageMaxWords} words) for describing the experience.`;
    } else if (hasLeftPageText && hasRightPageText) {
      aiHint += ` Offers text areas on both pages (~${leftPageMaxWords} words left, ~${rightPageMaxWords} words right) for comprehensive narrative.`;
    } else if (leftPageMaxWords > 0) { 
        aiHint += ` Text capacity: ~${leftPageMaxWords} words on left page.`;
    } else if (rightPageMaxWords > 0) {
        aiHint += ` Text capacity: ~${rightPageMaxWords} words on right page.`;
    }
    
    return {
      id: layout.id,
      totalPhotos,
      leftPagePhotoCount,
      rightPagePhotoCount,
      leftPageMaxWords, // New field directly on the layout object
      rightPageMaxWords, // New field directly on the layout object
      background: background,
      aiHint,
      leftPage: leftPageDescription,
      rightPage: rightPageDescription
    };
  });
}

/**
 * Generates cover page details (title, scattered words) using AI.
 * @param {Object} bestPhotoInfo - Information about the chosen cover photo.
 * @param {string} albumDateRange - Formatted date range of the album.
 * @param {string} summaryOfEntries - A summary of the album's content.
 * @param {Array<string>} keywordsForCover - List of prominent keywords/places.
 * @param {string} chatGptToken - OpenAI API token.
 * @param {Function} logAIFn - Logging function.
 * @returns {Promise<Object>} - { title, scatteredWordsFront, scatteredWordsBack }
 */
export async function generateCoverPageAIDetails(
  bestPhotoInfo,
  albumDateRange,
  summaryOfEntries,
  keywordsForCover,
  chatGptToken,
  logAIFn
) {
  if (logAIFn) logAIFn('Generating cover page AI details', { bestPhotoInfo, albumDateRange, summaryLength: summaryOfEntries.length, keywordsForCover });

  const prompt = `
    You are a creative assistant designing the cover for a family photo album.
    Here's information about the album:
    - Album Date Range: "${albumDateRange}"
    - Album Content Summary: "${summaryOfEntries}"
    - Key Themes/Keywords/Places: ${keywordsForCover.join(", ")}

    Your task is to generate:
    1.  title: A short, catchy, and heartwarming title for the album (max 5-7 words).
    2.  scatteredWordsFront: An array of 5 diverse and evocative short phrases (1-3 words each) for the front cover. These should complement the title and cover photo, reflecting the album's essence (e.g., locations, activities, emotions, dates/years if significant).
    3.  scatteredWordsBack: An array of 4 short phrases for the back cover, offering a thematic or concluding touch.
    Provide some info on why you chose the title and scattered words in the 'explanation' field.


    Return the result in JSON format:
    {
      "title": "string",
      "scatteredWordsFront": ["string", "string", "string", "string", "string"],
      "scatteredWordsBack": ["string", "string", "string", "string"],
      "explanation": "string"
    }
  `;

  if (logAIFn) {
    logAIFn('Calling OpenAI API for Cover prompt', { 
      type: 'AI_REQUEST',
      requestTime: new Date().toISOString(),
      model: "o4-mini", 
      promptLength: prompt.length,
      promptPreview: prompt.substring(0, 300),
      fullPrompt: prompt
    });
  }

  const requestBody = {
    model: "o4-mini", // Or your preferred model
    response_format: { "type": "json_object" },
    reasoning_effort: "medium",
    messages: [
      { role: "system", content: "You are a helpful assistant that designs photo album covers, designed to output JSON." },
      { role: "user", content: prompt },
    ],
    max_completion_tokens: 5000,
  };
  const config = { headers: { Authorization: `Bearer ${chatGptToken}` } };

  try {
    const response = await axios.post("https://api.openai.com/v1/chat/completions", requestBody, config);
    
    // Check if response has the expected structure
    if (!response.data || !response.data.choices || !response.data.choices[0] || !response.data.choices[0].message) {
      throw new Error('Invalid OpenAI response structure');
    }
    
    const messageContent = response.data.choices[0].message.content;
    if (!messageContent) {
      throw new Error('Empty message content from OpenAI');
    }
    
    const result = JSON.parse(messageContent);
    
    // Check if result is valid
    if (!result || typeof result !== 'object') {
      throw new Error('Invalid JSON result from OpenAI');
    }
    
    if (logAIFn) logAIFn('OpenAI Cover Response', { 
      type: 'AI_RESULT_PROCESSING',
      timestamp: new Date().toISOString(),
      result
    });
    
    // Ensure arrays have the expected length, padding with empty strings if necessary
    result.scatteredWordsFront = (result.scatteredWordsFront || []).slice(0, 5);
    while(result.scatteredWordsFront.length < 5) result.scatteredWordsFront.push("");
    result.scatteredWordsBack = (result.scatteredWordsBack || []).slice(0, 4);
    while(result.scatteredWordsBack.length < 4) result.scatteredWordsBack.push("");
    
    // Ensure title exists
    if (!result.title) {
      result.title = "Our Adventures";
    }

    return result;
  } catch (error) {
    console.error("Error calling OpenAI API for cover details:", error.response?.data || error.message || error);
    if (logAIFn) logAIFn('OpenAI Cover API Error', { 
      error: error.response?.data || error.message || error.toString(),
      errorType: error.constructor.name 
    });
    // Fallback default
    return {
      title: "Our Adventures",
      scatteredWordsFront: ["Memories", "Fun Times", "Journey", "Smiles", albumDateRange.split(" - ")[0].split(", ")[1] || ""],
      scatteredWordsBack: ["The Story", "Continues", "Love", "Us"],
      explanation: "Generated from fallback due to API error"
    };
  }
}
